<template>
  <div class="center light-bg">
    <div class="top-container battery-container">
      <span>电量</span>
      <img :src="batteryIcon" class="battery-icon" />
      <span>{{ `${parseInt(data.battery)}%` }}</span>
    </div>
    <div class="time-container top-container" @click="copyToClipboard">
      {{ data.date }} {{ data.time }}
    </div>
    <div>
      <img :src="turnLeftIcon" class="turn-icon left" :class="data.turnLeftState" />
      <img :src="turnLeftIcon" class="turn-icon right" :class="data.turnRightState" />
    </div>
    <div class="number speed">
      <small>定位速度</small>
      <span>
        {{ data.linearVelocity }}
      </span>
      <small>m/s</small>
    </div>
    <div class="number acceleration">
      <small>加速度</small>
      <span>
        {{ data.acceleration }}
      </span>

      <small>m/s²</small>
    </div>
    <div class="gear-box">
      <swiper :slides-per-view="5" :centered-slides="true" @swiper="onGearPositionMapSwiper">
        <swiper-slide class="gear" v-for="item in GearPositionMap" :key="item.value" :class="item.class">
          {{ item.text }}
        </swiper-slide>
      </swiper>
    </div>
    <div class="planning-title" :class="data.planningType">
      {{ data.showDriveMode }}<span style="color: #d40c0c;"> {{ data.shieldDrivingMode }}</span>
    </div>
    <div class="planning-icon">
      <el-tooltip content="vehicleSignalCtrl_horn">
        <el-icon>
          <BellFilled :color="data.vehicleSignalCtrl_horn" />
        </el-icon>
      </el-tooltip>
      <el-tooltip content="VehicleSignal_brakeLamp">
        <el-icon>
          <Opportunity :color="data.VehicleSignal_brakeLamp" />
        </el-icon>
      </el-tooltip>
    </div>
    <div class="routing-title">
      {{ data.routingMode }}
    </div>
    <div class="steer-angle">
      <SteerAngle />
    </div>
  </div>
</template>

<script>
import { defineComponent, watch, ref, computed } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import '@/assets/css/iconfont.css';
import { GearPositionMap } from '@/store/modules/ws/types';
import { useWsModule, useStore } from '@/store/index';
import { getNameByValueForEnum } from '@/proto/helper.js';
import { getLatestRemoteControl } from '@/service';
import { dayjs, ElDialog, ElTooltip, ElIcon } from 'element-plus';
import { BellFilled, Opportunity } from '@element-plus/icons-vue'
import batteryIcon from '@/assets/images/battery.png';
import SteerAngle from './SteerAngle.vue';
import turnLeftIcon from '@/assets/images/turn-left.png';
import { useFrameDataStore } from "@/store/frameDataStore";

export default defineComponent({
  name: 'Dashboard',
  components: {
    Swiper,
    SwiperSlide,
    SteerAngle,
    ElTooltip,
    ElIcon,
    Opportunity,
    BellFilled
  },
  setup() {
    let frameDataStore = useFrameDataStore();
    let data = ref({
      date: "",
      time: "",
      battery: 0,
      acceleration: 0,
      linearVelocity: 0,
      gearPosition: 0,
      showDriveMode: "",
      shieldDrivingMode: "",
      planningType: "",
      vehicleSignalCtrl_horn: "#aaa",
      VehicleSignal_brakeLamp: "#aaa",
      routingMode: "机非混合模式",
    });
    let swiperController = null;
    const error = [
      'NODATA',
      'UNKNOWN',
      'VANGUARD_NORMAL',
      'VANGUARD_EMERGENCY',
      'VANGUARD_FORBID_DRIVING',
      'CHASSIS_BUMPER',
      'CHASSIS_FAULT',
      'EMERGENCY_STOP',
    ];
    const success = ['AUTO_CTRL'];

    // 远程控制模式（REMOTE_CTRL=5 / REMOTE_SUPER_CTRL=8）需展示接管来源（监控接管 / APP接管）
    const REMOTE_CONTROL_MODES = [5, 8];
    const remoteControlSuffix = ref('');
    let lastRemoteControlMode = null;
    let currentDrivingMode = 0;

    // 统一构造模式 label：远程控制模式且已取到来源时拼接后缀
    const buildDriveModeLabel = (drivingMode) => {
      const baseLabel = getNameByValueForEnum('Vehicle.DrivingMode', drivingMode);
      if (REMOTE_CONTROL_MODES.includes(drivingMode) && remoteControlSuffix.value) {
        return `${baseLabel}(${remoteControlSuffix.value})`;
      }
      return baseLabel;
    };

    const fetchRemoteControlSuffix = async (vehicleName, timestampMs, drivingMode) => {
      if (!vehicleName || !timestampMs) return;
      if (drivingMode === lastRemoteControlMode) return;
      lastRemoteControlMode = drivingMode;

      // 帧时间戳为毫秒，接口需要秒级 operateTimestamp
      const operateTimestamp = Math.trunc(timestampMs / 1000);
      try {
        const res = await getLatestRemoteControl({ vehicleName, operateTimestamp });
        // operateRequest 已解包为 { data, success }；soure_name 为来源原始值（如 "APP" / "监控"）
        const soureName = res?.success ? (res.data?.soure_name || '') : '';
        remoteControlSuffix.value = soureName ? `${soureName}接管` : '';
      } catch (e) {
        remoteControlSuffix.value = '';
      }
      // 接口返回后若仍处于远控模式，补上后缀（兼容暂停单帧场景）
      if (REMOTE_CONTROL_MODES.includes(currentDrivingMode)) {
        data.value.showDriveMode = buildDriveModeLabel(currentDrivingMode);
      }
    };

    watch(() => frameDataStore.currentData, (e) => {
      if (!e) return;
      data.value.date = dayjs(e.timestamp).format('YYYY/MM/DD');
      data.value.time = dayjs(e.timestamp).format('HH:mm:ss:SSS');

      if (e?.vehicleData) {
        data.value.linearVelocity = Number(e.vehicleData?.localization?.linearVelocity?.x).toFixed(2);
      }
      if (e?.vehicleData?.vehicle) {
        let vehicle = e?.vehicleData?.vehicle;
        data.value.battery = e.vehicleData.vehicle?.battery;
        switch (vehicle?.turnSignalState) {
          case 1:
            data.value.turnLeftState = 'active both';
            data.value.turnRightState = '';
            break;
          case 2:
            data.value.turnLeftState = '';
            data.value.turnRightState = 'active both';
            break;
          case 3:
            data.value.turnLeftState = data.value.turnRightState = 'active both';
            break;
          default:
            data.value.turnLeftState = data.value.turnRightState = '';
        }
        if (e?.vehicleData?.localization?.linearAcceleration) {
          data.value.acceleration = Number(e?.vehicleData?.localization?.linearAcceleration?.x).toFixed(2);
        }

        data.value.gearPosition = vehicle?.gearPosition
        if (swiperController) {
          swiperController.slideTo(GearPositionMap[vehicle?.gearPosition].value);
        }
        {
          //showDriveMode
          const drivingMode = vehicle?.drivingMode;
          currentDrivingMode = drivingMode;
          const baseLabel = getNameByValueForEnum('Vehicle.DrivingMode', drivingMode);
          if (REMOTE_CONTROL_MODES.includes(drivingMode)) {
            // 触发接口获取接管来源（内部按 drivingMode 缓存，同一模式持续时不重复请求）
            fetchRemoteControlSuffix(frameDataStore.currentVehicleId, e.timestamp, drivingMode);
          } else {
            // 离开远控模式清空后缀
            remoteControlSuffix.value = '';
            lastRemoteControlMode = null;
          }
          data.value.showDriveMode = buildDriveModeLabel(drivingMode);
          if (vehicle?.shieldDrivingMode) {
            data.value.shieldDrivingMode = "-ShieldStop";
          }else{
            data.value.shieldDrivingMode = "";
          }
          // planningType 用不带后缀的原始 label 匹配颜色
          if (error.includes(baseLabel)) {
            data.value.planningType = 'error';
          } else if (success.includes(baseLabel)) {
            data.value.planningType = 'success';
          } else {
            data.value.planningType = 'warning';
          }
        }
        {
          if (e?.vehicleData?.routing?.routerEdition === 2) {
            data.value.routingMode = '机动车道模式';
          } else if (e?.vehicleData?.routing?.routerEdition === 1) {
            data.value.routingMode = '机非混合模式';
          } else {
            data.value.routingMode = '';
          }
        }
        {
          //鸣笛
          if (e?.vehicleData?.planning?.vehicleSignalCtrl) {
            data.value.vehicleSignalCtrl_horn = e?.vehicleData?.planning?.vehicleSignalCtrl?.horn == 1 ? "#08f543" : "#aaa"
          }
          //喇叭
          if (e?.vehicleData?.vehicleMotion?.vehicleSignal?.brakeLamp) {
            data.value.VehicleSignal_brakeLamp = e?.vehicleData?.vehicleMotion?.vehicleSignal?.brakeLamp == 2 ? "#d71345" : "#aaa"
          }
        }
      }
    })
    const onGearPositionMapSwiper = (swiper) => {
      swiperController = swiper;
    };
    const copyToClipboard = () => {
      const content = `${data.value.date} ${data.value.time}`;
      navigator.clipboard.writeText(content).then(() => {
        ElMessage.success('复制当前时间成功');
      });
    }
    return {
      onGearPositionMapSwiper,
      GearPositionMap,
      data,
      batteryIcon,
      turnLeftIcon,
      copyToClipboard
    };
  },
});
</script>
<style lang="less" scoped>
.center {
  position: relative;
  margin: 0;
  float: left;
  width: 486px;
  height: 100%;
  // pointer-events: none;

  .top-container {
    height: 33px;
    position: absolute;
    top: 5px;
    padding-top: 11px;
    text-align: center;
    font-size: 16px;
    color: #ffffff;
    box-sizing: border-box;
    font-family: DINPro;
  }

  .battery-container {
    width: 134px;
    background: url(@/assets/images/left-bg.png) no-repeat;
    background-size: 100% 100%;
    left: 12px;

    .battery-icon {
      width: 30px;
      height: 18px;
      transform: translateY(3px);
    }
  }

  .time-container {
    width: 212px;
    background: url(@/assets/images/right-bg.png) no-repeat;
    background-size: 100% 100%;

    right: 12px;
  }

  .turn-icon {
    position: absolute;
    top: 57px;
    width: 20px;
    opacity: 0.4;
  }

  .left {
    left: 146px;
  }

  .right {
    transform: scaleX(-1);
    right: 146px;
  }

  .active {
    opacity: 1;
  }

  .both {
    animation: flash 0.3s infinite linear;
  }

  .number {
    position: absolute;
    top: 90px;
    height: 91px;
    width: 91px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    color: #fff;
    font-weight: 500;
    font-size: 26px;
    color: #fdff3f;
    background: url(@/assets/images/circle.png) no-repeat;
    background-size: 100% 100%;
    box-sizing: border-box;
    padding: 18px 0;

    small {
      color: #fff;
      font-size: 12px;
      font-weight: 400;
    }

    span {
      height: 26px;
      display: flex;
      align-items: center;
    }
  }

  .speed {
    left: 52px;
  }

  .acceleration {
    right: 52px;
  }

  .gear-box {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;

    .gear {
      display: flex;
      justify-content: center;
      transform: scale(0.8);
      font-size: 18px;
      color: #fff;
      border: 1px solid transparent;
      border-radius: 2px;
      user-select: none;

      &.swiper-slide-active {
        transform: scale(1);
        background: rgba(48, 221, 255, 0.4);
        color: #16ffff;
        border-radius: 3.6px;
      }
    }
  }
}

@keyframes flash {

  0%,
  50% {
    opacity: 0.4;
  }

  51%,
  100% {
    opacity: 1;
  }
}

.planning-title {
  position: absolute;
  bottom: 12px;
  left: 8px;
  // transform: translateX(-50%);
  text-align: center;
  color: #fff;

  &.error {
    color: #d40c0c;
  }

  &.success {
    color: #08f543;
  }

  &.warning {
    color: #b0d010;
  }
}

.planning-icon {
  position: absolute;
  bottom: 12px;
  right: 30px;
  // transform: translateX(-50%);
  text-align: center;
  color: #fff;

  &.error {
    color: #d40c0c;
  }

  &.success {
    color: #08f543;
  }

  &.warning {
    color: #b0d010;
  }
}

.routing-title {
  font-size: 14px;
  position: absolute;
  bottom: 40px;
  right: 55px;
  text-align: center;
  color: #fff;
}

.steer-angle {
  position: absolute;
  top: 33px;
  left: 50%;
  transform: translateX(-50%);
  width: 118px;
  height: 148px;
}
</style>
